import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import svgIcons from "../../assets/svg/svgIcons";
import FTModal from "../../components/common/FTModal";
import {
  classifyTrip,
  TRIP_CLASSIFICATION_LABEL_KEY,
} from "../map-asset/multiDay/tripClassification";
import { tripKeyFor } from "../map-asset/multiDay/renderMultiVehicleRoutes";
import { VehicleGroup } from "./useMultiVehicleTripData";

type Props = {
  visible: boolean;
  vehicleGroups: VehicleGroup[];
  selectedKeys: string[];
  // the cap is on side-by-side maps, i.e. vehicles - an asset contributes
  // one card however many of its trips are picked
  maxVehicles: number;
  onHide: () => void;
  onConfirm: (keys: string[]) => void;
};

export const CompareVehicleRoutesModal: FC<Props> = ({
  visible,
  vehicleGroups,
  selectedKeys,
  maxVehicles,
  onHide,
  onConfirm,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "vehicleMap.tripDetails",
  });
  const [working, setWorking] = useState<string[]>(selectedKeys);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  if (!visible) return null;

  const toggleExpanded = (deviceId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });

  const deviceIdOf = (tripKey: string) => tripKey.split("__")[0];
  const devicesIn = (keys: string[]) => new Set(keys.map(deviceIdOf));
  const selectedDeviceCount = devicesIn(working).size;

  // room for another card, unless this vehicle already has one
  const canAddDevice = (deviceId: string) =>
    devicesIn(working).has(deviceId) || selectedDeviceCount < maxVehicles;

  const toggleTrip = (tripKey: string) =>
    setWorking((prev) => {
      if (prev.includes(tripKey)) return prev.filter((key) => key !== tripKey);
      if (!canAddDevice(deviceIdOf(tripKey))) return prev;
      return [...prev, tripKey];
    });

  const tripKeysOf = (group: VehicleGroup) =>
    group.trips.map((_, tripIndex) => tripKeyFor(group.deviceId, tripIndex));

  // comparing one asset's whole day is as common as comparing across
  // vehicles, so a vehicle can be taken in one go - all its trips land on
  // that vehicle's single map, so the count doesn't matter
  const toggleVehicle = (group: VehicleGroup) => {
    const keys = tripKeysOf(group);
    setWorking((prev) => {
      const isFullySelected =
        keys.length > 0 && keys.every((key) => prev.includes(key));
      if (isFullySelected) return prev.filter((key) => !keys.includes(key));
      if (!canAddDevice(group.deviceId)) return prev;
      return Array.from(new Set([...prev, ...keys]));
    });
  };

  const vehicleSelectionState = (group: VehicleGroup) => {
    const keys = tripKeysOf(group);
    if (keys.length === 0) return "empty" as const;
    const chosen = keys.filter((key) => working.includes(key));
    if (chosen.length === 0) return "none" as const;
    return chosen.length === keys.length ? ("all" as const) : ("some" as const);
  };

  return (
    <FTModal
      visible={visible}
      onHide={onHide}
      position="center"
      containerStyle="max-w-[calc(100vw-4rem)] w-[480px]"
      heading="Compare Routes"
      nodeLeftOfCloseButton={
        <span className="text-xs font-medium text-heading-gray888 mr-3 shrink-0">
          {selectedDeviceCount} of {maxVehicles} Vehicles
        </span>
      }
    >
      <p className="text-xs text-heading-gray888 -mt-2 mb-3">
        Compare up to {maxVehicles} vehicles side by side. Every trip picked
        for a vehicle is drawn on that vehicle&apos;s own map.
      </p>

      <div className="max-h-[50vh] overflow-auto -mx-2">
        {vehicleGroups.map((group) => {
          const isExpanded = expanded.has(group.deviceId);
          return (
            <div key={group.deviceId} className="border-b border-gray-200">
              <div className="w-full flex items-center gap-2 px-2 hover:bg-accent-blue-paleO66">
                <input
                  type="checkbox"
                  aria-label={`Select all trips for ${group.fullLabel}`}
                  className="size-4 accent-accent-blue-dark cursor-pointer disabled:cursor-not-allowed shrink-0"
                  checked={vehicleSelectionState(group) === "all"}
                  disabled={
                    vehicleSelectionState(group) === "empty" ||
                    !canAddDevice(group.deviceId)
                  }
                  ref={(el) => {
                    if (el)
                      el.indeterminate = vehicleSelectionState(group) === "some";
                  }}
                  onChange={() => toggleVehicle(group)}
                />
                <button
                  type="button"
                  onClick={() => toggleExpanded(group.deviceId)}
                  className="flex-grow flex items-center justify-between py-2.5 min-w-0"
                >
                  <span className="flex items-center gap-2 font-semibold text-sm min-w-0">
                    <span
                      className="inline-block size-3 rounded-full shrink-0"
                      style={{ backgroundColor: group.color }}
                    />
                    <span className="truncate">{group.fullLabel}</span>
                    <span className="text-heading-gray888 font-normal shrink-0">
                      ({group.trips.length} Trip
                      {group.trips.length === 1 ? "" : "s"})
                    </span>
                  </span>
                  <img
                    src={svgIcons.LeftArrowIcon}
                    alt=""
                    className={`w-3 h-3 ms-2 shrink-0 transition-transform ${isExpanded ? "rotate-90" : "-rotate-90"}`}
                  />
                </button>
              </div>

              {isExpanded && (
                <div className="pb-2">
                  {group.trips.map((tripPoints, tripIndex) => {
                    const tripKey = tripKeyFor(group.deviceId, tripIndex);
                    const isChecked = working.includes(tripKey);
                    const isDisabled =
                      !isChecked && !canAddDevice(group.deviceId);
                    const label = t(
                      TRIP_CLASSIFICATION_LABEL_KEY[classifyTrip(tripPoints)]
                    );
                    return (
                      <label
                        key={tripKey}
                        className={`flex items-center gap-2 px-2 py-1.5 text-xs ${
                          isDisabled
                            ? "opacity-40 cursor-not-allowed"
                            : "cursor-pointer hover:bg-accent-blue-paleO66"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="size-4 accent-accent-blue-dark cursor-pointer disabled:cursor-not-allowed"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={() => toggleTrip(tripKey)}
                        />
                        <span
                          className="inline-block size-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: group.color }}
                        />
                        Trip {tripIndex + 1} ({label})
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        disabled={working.length === 0}
        onClick={() => onConfirm(working)}
        className="w-full mt-4 py-2.5 rounded-lg bg-accent-blue-pale text-accent-blue-dark font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed enabled:hover:bg-accent-blue-paleO66"
      >
        Compare ({working.length} trip{working.length === 1 ? "" : "s"} across{" "}
        {selectedDeviceCount} vehicle{selectedDeviceCount === 1 ? "" : "s"})
      </button>
      <button
        type="button"
        onClick={onHide}
        className="w-full mt-2 py-1 text-sm font-medium text-field-error-dark"
      >
        Cancel
      </button>
    </FTModal>
  );
};
